/**
 * Sentry Client Configuration
 * 
 * Configures Sentry for client-side error tracking in the interview platform.
 * This file is loaded in the browser and handles client-side errors.
 */

import * as Sentry from '@sentry/react';

/**
 * Initialize Sentry for client-side
 * Should be called before React app initialization
 */
export function initSentry(): void {
  // Get DSN from environment - use VITE_ prefix for client-side access
  const dsn = import.meta.env.VITE_SENTRY_DSN || import.meta.env.SENTRY_DSN;
  
  // Only initialize if DSN is provided
  if (!dsn) {
    if (import.meta.env.DEV) { void 0 }
    return;
  }

  const environment = import.meta.env.MODE || import.meta.env.NODE_ENV || 'development';
  const release = import.meta.env.VITE_APP_VERSION || 'unknown';

  Sentry.init({
    dsn,
    environment,
    release,
    
    // Performance monitoring
    tracesSampleRate: environment === 'production' ? 0.1 : 1.0,
    
    // Session replay (optional, can be enabled later)
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: 0,
    
    // Filter sensitive data before sending
    beforeSend(event, hint) {
      // Filter out sensitive information
      if (event.request) {
        // Remove sensitive headers
        if (event.request.headers) {
          delete event.request.headers['authorization'];
          delete event.request.headers['cookie'];
        }
        
        // Remove sensitive query parameters
        if (event.request.query_string) {
          const queryParams = new URLSearchParams(event.request.query_string);
          queryParams.delete('token');
          queryParams.delete('apiKey');
          event.request.query_string = queryParams.toString();
        }
      }
      
      // Filter sensitive user data
      if (event.user) {
        // Only keep safe user identifiers
        const safeUser = {
          id: event.user.id,
          username: event.user.username,
        };
        event.user = safeUser;
      }
      
      return event;
    },
    
    // Integrate with React
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],
    
    // Ignore common browser extension errors
    ignoreErrors: [
      // Browser extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      'atomicFindClose',
      'fb_xd_fragment',
      'bmi_SafeAddOnload',
      'EBCallBackMessageReceived',
      'conduitPage',
      // Network errors that are not actionable
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // Chrome extension errors
      'chrome-extension://',
      'moz-extension://',
    ],
    
    // Don't send errors in development unless explicitly enabled
    enabled: environment !== 'development' || !!import.meta.env.VITE_SENTRY_ENABLE_DEV,
  });
}

/**
 * Set user context for error tracking
 */
export function setSentryUser(user: {
  id?: string;
  email?: string;
  username?: string;
  interviewId?: string;
}): void {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
  
  // Add interview-specific context
  if (user.interviewId) {
    Sentry.setTag('interview_id', user.interviewId);
  }
}

/**
 * Set additional context for error tracking
 */
export function setSentryContext(context: {
  interviewId?: string;
  candidateName?: string;
  jobTitle?: string;
  sessionStatus?: string;
}): void {
  if (context.interviewId) {
    Sentry.setTag('interview_id', context.interviewId);
  }
  if (context.candidateName) {
    Sentry.setTag('candidate_name', context.candidateName);
  }
  if (context.jobTitle) {
    Sentry.setTag('job_title', context.jobTitle);
  }
  if (context.sessionStatus) {
    Sentry.setTag('session_status', context.sessionStatus);
  }
  
  Sentry.setContext('interview_session', context);
}

/**
 * Clear user context
 */
export function clearSentryUser(): void {
  Sentry.setUser(null);
}




